import { useCallback, useEffect, useState } from "react";
import { fetchTransferDetail } from "@/lib/api";
import type { TransferDetail } from "@/types";
import { transferStatusLabel, transferStatusState } from "@/features/transfers/transfer-state";
import { mapApiTransfer } from "@/hooks/use-transfers";

interface UseTransferDetailResult {
  detail: TransferDetail | null;
  loading: boolean;
  error: string | null;
  source: "api";
  refresh: () => void;
}

export function mapApiTransferDetail(raw: Record<string, unknown>): TransferDetail {
  const id = String(raw.id ?? raw.transferId ?? "");
  const sourceWallet = (raw.sourceWallet ?? {}) as Record<string, unknown>;
  const destination = (raw.destination ?? {}) as Record<string, unknown>;
  const source = String(sourceWallet.name ?? sourceWallet.id ?? "");
  const dest = String(destination.address ?? "");
  const asset = String(raw.asset ?? "");
  // An amount the gateway never sent is unknown, not zero: coercing it to "0"
  // printed a fabricated number in the detail page's headline money cell. A real
  // 0 (string or number) is a known value and is kept as one.
  const rawAmount = raw.amount;
  const amountKnown = rawAmount !== undefined && rawAmount !== null && rawAmount !== "";
  const amountMain = amountKnown ? `${String(rawAmount)} ${asset}`.trim() : "—";
  const statusRaw = String(raw.status ?? "draft");

  const timeline = Array.isArray(raw.timeline)
    ? (raw.timeline as Record<string, unknown>[]).map(e => {
        const state = String(e.state ?? "");
        return {
          title: transferStatusLabel(state) || state,
          meta: typeof e.at === "string" ? e.at : undefined,
          detail: undefined as string | undefined,
          tone: (state.includes("pending") ? "active" : "success") as "success" | "active" | "danger" | "pending",
        };
      })
    : [];

  const failure =
    typeof raw.failureReason === "string" && raw.failureReason
      ? {
          banner: raw.failureReason,
          failurePoint: typeof raw.failureService === "string" ? raw.failureService : "—",
          error: raw.failureReason,
          options: [],
        }
      : undefined;

  // The gateway's TransferDetailResponse.TransferView carries `transactionHash` once the
  // governed intent reaches BROADCAST_SUBMITTED/CONFIRMED (see WalletKitGrpcService /
  // GrpcGatewayWalletKitClient#toGatewayIntentState in alloy-api-gateway-service). Surface it
  // so the finality panel can link to Sepolia Etherscan instead of only ever showing mock data.
  const txHash = typeof raw.transactionHash === "string" ? raw.transactionHash : "";
  const finality = txHash ? { block: "—", confirmations: "—", txHash, gasUsed: "—" } : undefined;

  return {
    id,
    direction: "send",
    amountMain,
    amountUsd: "",
    source,
    destination: dest,
    createdBy: "",
    providerRoute: "",
    defaultState: transferStatusState(statusRaw),
    timeline,
    evidence: [],
    failure,
    finality,
    record: {
      transfer: mapApiTransfer(raw),
      timeline: Array.isArray(raw.timeline)
        ? (raw.timeline as unknown[])
            .filter((e): e is Record<string, unknown> => !!e && typeof e === "object")
            .map((e) => ({ state: String(e.state ?? ""), at: typeof e.at === "string" ? e.at : "" }))
            .filter((e) => e.state)
        : [],
      failureReason: typeof raw.failureReason === "string" ? raw.failureReason : "",
      failureService: typeof raw.failureService === "string" ? raw.failureService : "",
      degraded: raw.degraded === true,
    },
  };
}

export function useTransferDetail(id: string): UseTransferDetailResult {
  const [detail, setDetail] = useState<TransferDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<"api">("api");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchTransferDetail(id);
      if (result.transfer) {
        setDetail(mapApiTransferDetail(result.transfer));
        setSource("api");
      } else {
        setError(result.reasonCode || "Transfer not found");
        setDetail(null);
      }
    } catch {
      setError("Failed to load transfer");
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  return { detail, loading, error, source, refresh: load };
}
